import each from 'lodash/fp/each';
import find from 'lodash/fp/find';
import noop from 'lodash/fp/noop';

export const NAVIGATION_OPEN_CLASS =
  'desktop-navigation__dropdown-content--open';

export default class DesktopKeyboardNavigation {
  constructor(element, showOverlay = noop, hideOverlay = noop) {
    this.element = element;
    this.eventHandlers = [];
    this.timers = [];
    this.showOverlay = showOverlay;
    this.hideOverlay = hideOverlay;

    if (!!this.element) {
      this.dropdowns = this.element.querySelectorAll(
        '[data-js="desktop-navigation-dropdown"]'
      );
      this.primaryNavItems = this.element.querySelectorAll(
        '[data-js="desktop-navigation-primary-item"]'
      );

      this.attachEvents();
    }
  }

  attachEvent = (target, eventName, handler) => {
    this.eventHandlers.push({ target, eventName, handler });
    target.addEventListener(eventName, handler);
  };

  anyDropdownIsOpen = () => find(this.dropdownIsOpen)(this.dropdowns);

  clearTimers = () => {
    each(clearTimeout)(this.timers);
    this.timers = [];
  };

  closeDropdowns() {
    each(this.closeDropdown)(this.dropdowns);
  }

  dropdownIsOpen = dropdown =>
    dropdown.classList.contains(NAVIGATION_OPEN_CLASS);

  getDropdown = primaryNavItem =>
    primaryNavItem.querySelector('[data-js="desktop-navigation-dropdown"]');

  toggleDropdown = dropdown => {
    const isOpen = this.dropdownIsOpen(dropdown);

    if (isOpen) {
      this.closeDropdown(dropdown);
      return;
    }

    // Make sure we close other dropdowns before opening this one. We don't want
    // more than one dropdown open at the same time.
    this.closeDropdowns();
    this.openDropdown(dropdown);
  };

  toggleDropdownIfExists = event => {
    const dropdown = this.getDropdown(event.target.parentElement);

    if (dropdown) {
      this.toggleDropdown(dropdown);
      return dropdown;
    }
  };

  openDropdown = dropdown => {
    const anchorOrButtonElement = dropdown.parentElement.querySelector(
      'a, button'
    );

    if (!this.dropdownIsOpen(dropdown)) {
      dropdown.classList.add(NAVIGATION_OPEN_CLASS);
      anchorOrButtonElement.setAttribute('aria-expanded', 'true');
      this.showOverlay();
    }
  };

  closeDropdown = dropdown => {
    const anchorOrButtonElement = dropdown.parentElement.querySelector(
      'a, button'
    );

    if (this.dropdownIsOpen(dropdown)) {
      anchorOrButtonElement.setAttribute('aria-expanded', 'false');
      dropdown.classList.remove(NAVIGATION_OPEN_CLASS);
      this.hideOverlay();
    }
  };

  handleContentLinkItems = ({ target }) => {
    const anchorOrButtonElement = target.closest('a, button');

    if (anchorOrButtonElement) {
      this.closeDropdowns();
    }
  };

  handleMouseEnter = event => {
    const dropdown = this.getDropdown(event.target);

    const open = () => {
      this.closeDropdowns();
      this.openDropdown(dropdown);
    };

    if (dropdown && !this.dropdownIsOpen(dropdown)) {
      // if another dropdown is open, delay opening this one in case the user is trying
      // to enter that dropdown instead of opening this one.
      if (this.anyDropdownIsOpen()) {
        this.clearTimers();
        this.timers.push(setTimeout(open, 150));
        return;
      }

      this.clearTimers();
      open();
    }
  };

  handleMouseLeave = event => {
    const dropdown = this.getDropdown(event.target);

    if (dropdown) {
      this.timers.push(
        setTimeout(() => {
          this.closeDropdown(dropdown);
        }, 150)
      );
    }
  };

  handleKeyUp = event => {
    if (
      event.key === 'Tab' &&
      !event.target.closest('[data-js="desktop-navigation-dropdown"]')
    ) {
      this.closeDropdowns();
    }
  };

  handleKeyDown = event => {
    if (event.key !== 'Enter') return;

    const dropdown = this.toggleDropdownIfExists(event);
    if (dropdown) event.preventDefault();
  };

  handlePrimaryNavItemTouch = event => {
    const dropdown = this.toggleDropdownIfExists(event);

    // We want to prevent touch events from bubbling up to the click event so
    // that touch screen users can open dropdowns without navigating to links
    // associated with the navigation item they clicked.
    if (dropdown) event.preventDefault();
  };

  handleTouch = event => {
    if (!event.target.closest('[data-js="desktop-navigation"]')) {
      this.closeDropdowns();
    }
  };

  handleClick = this.toggleDropdownIfExists;

  attachEvents() {
    // Close all open dropdown if the user hit Tab and it moves focus outside of
    // the dropdown.
    this.attachEvent(document, 'keyup', this.handleKeyUp);
    this.attachEvent(document, 'touchend', this.handleTouch);

    // For each primary item, we need to support three different types of input:
    // keyboard, touch, and mouse. Keyboard and touch screen users need to be
    // able to toggle the dropdown without clicking on any links that might be
    // associated with the primary item. Mouse users can click on the primary
    // item if it has a link associated with it, while being able to toggle
    // the dropdown via hover.
    each(primaryItem => {
      this.attachEvent(primaryItem, 'mouseenter', this.handleMouseEnter);
      this.attachEvent(primaryItem, 'mouseleave', this.handleMouseLeave);

      const anchorOrButtonElement = primaryItem.querySelector('a, button');

      this.attachEvent(anchorOrButtonElement, 'keydown', this.handleKeyDown);
      this.attachEvent(
        anchorOrButtonElement,
        'touchend',
        this.handlePrimaryNavItemTouch
      );
      this.attachEvent(anchorOrButtonElement, 'click', this.handleClick);
    })(this.primaryNavItems);

    each(dropdown => {
      this.attachEvent(dropdown, 'click', this.handleContentLinkItems);
    })(this.dropdowns);
  }

  detachEvents() {
    each(({ target, eventName, handler }) =>
      target.removeEventListener(eventName, handler)
    )(this.eventHandlers);
    this.eventHandlers = [];
    this.clearTimers();
  }
}
